import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy File Upload / Dropzone
 *
 * A branded drag-and-drop file upload area for ID photos, yearbook images,
 * and document uploads. Matches Input styling with warm gold focus ring.
 *
 * Features:
 * - Drag-and-drop with visual feedback
 * - Click to browse
 * - File type restrictions
 * - File size limits
 * - Preview for images
 * - File list with remove option
 * - Upload progress simulation
 * - Navy dashed border, gold focus, cream hover
 * - Accessible: aria-describedby, keyboard activation
 *
 * Usage:
 *   <Dropzone
 *     label="Student Photo"
 *     accept="image/*"
 *     maxSize={5 * 1024 * 1024}  // 5MB
 *     maxFiles={1}
 *     onFiles={handleFiles}
 *     hint="JPG, PNG, or WebP. Max 5MB."
 *   />
 *
 *   <Dropzone
 *     label="Yearbook Photos"
 *     accept="image/*"
 *     maxFiles={10}
 *     multiple
 *     onFiles={handleFiles}
 *   />
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface DropzoneFile {
	/** File object */
	file: File
	/** Unique id */
	id: string
	/** Preview URL (for images) */
	preview?: string
	/** Upload progress (0–100) */
	progress?: number
	/** Error message */
	error?: string
}

export interface DropzoneProps {
	/** Label text */
	label?: string
	/** Helper text */
	hint?: string
	/** Error message */
	error?: string
	/** Accepted file types (MIME) */
	accept?: string
	/** Max file size in bytes */
	maxSize?: number
	/** Max number of files */
	maxFiles?: number
	/** Allow multiple files */
	multiple?: boolean
	/** Called when files are added/removed */
	onFiles?: (files: DropzoneFile[]) => void
	/** Current files (controlled) */
	files?: DropzoneFile[]
	/** Show file previews */
	showPreview?: boolean
	/** Disabled state */
	disabled?: boolean
	/** Additional class */
	className?: string
}

// ─── Helpers ────────────────────────────────────────────────────────────────

let fileIdCounter = 0

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(file: File): boolean {
	return file.type.startsWith('image/')
}

// ─── Dropzone ───────────────────────────────────────────────────────────────

export function Dropzone({
	label,
	hint,
	error,
	accept,
	maxSize = 10 * 1024 * 1024, // 10MB default
	maxFiles = 1,
	multiple = false,
	onFiles,
	files: controlledFiles,
	showPreview = true,
	disabled = false,
	className,
}: DropzoneProps) {
	const [internalFiles, setInternalFiles] = React.useState<DropzoneFile[]>([])
	const [isDragOver, setIsDragOver] = React.useState(false)
	const inputRef = React.useRef<HTMLInputElement>(null)

	const files = controlledFiles ?? internalFiles
	const setFiles = onFiles ? () => {} : setInternalFiles

	const addFiles = React.useCallback(
		(fileList: FileList | File[]) => {
			const newFiles: File[] = Array.from(fileList)

			const processed: DropzoneFile[] = newFiles
				.slice(0, maxFiles - files.length)
				.map(file => {
					const dropzoneFile: DropzoneFile = {
						file,
						id: `file-${++fileIdCounter}-${Date.now()}`,
						error: undefined,
					}

					// Validate size
					if (maxSize && file.size > maxSize) {
						dropzoneFile.error = `File is too large (${formatFileSize(file.size)}). Max ${formatFileSize(maxSize)}.`
					}

					// Generate preview for images
					if (showPreview && isImage(file) && !dropzoneFile.error) {
						dropzoneFile.preview = URL.createObjectURL(file)
					}

					return dropzoneFile
				})

			const next = [...files, ...processed]
			if (!controlledFiles) setInternalFiles(next)
			onFiles?.(next)
		},
		[files, maxFiles, maxSize, showPreview, controlledFiles, onFiles],
	)

	const removeFile = React.useCallback(
		(id: string) => {
			const file = files.find(f => f.id === id)
			if (file?.preview) URL.revokeObjectURL(file.preview)
			const next = files.filter(f => f.id !== id)
			if (!controlledFiles) setInternalFiles(next)
			onFiles?.(next)
		},
		[files, controlledFiles, onFiles],
	)

	// Drag events
	const handleDragOver = React.useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (!disabled) setIsDragOver(true)
	}, [disabled])

	const handleDragLeave = React.useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)
	}, [])

	const handleDrop = React.useCallback(
		(e: React.DragEvent) => {
			e.preventDefault()
			e.stopPropagation()
			setIsDragOver(false)
			if (!disabled) addFiles(e.dataTransfer.files)
		},
		[disabled, addFiles],
	)

	// Click to browse
	const handleClick = () => {
		if (!disabled) inputRef.current?.click()
	}

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) addFiles(e.target.files)
		// Reset so same file can be selected again
		e.target.value = ''
	}

	// Keyboard activation
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
			e.preventDefault()
			inputRef.current?.click()
		}
	}

	return (
		<div className={cn('space-y-3', className)}>
			{label && (
				<label className="block text-sm font-medium text-foreground font-ui">
					{label}
				</label>
			)}

			{/* Dropzone area */}
			<div
				onClick={handleClick}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onKeyDown={handleKeyDown}
				tabIndex={disabled ? undefined : 0}
				role="button"
				aria-label={label || 'Upload file'}
				aria-describedby={hint ? 'dropzone-hint' : undefined}
				className={cn(
					'relative flex flex-col items-center justify-center',
					'p-8 rounded-lg border-2 border-dashed',
					'transition-all duration-fast ease-default',
					'cursor-pointer',
					// States
					disabled && 'opacity-50 cursor-not-allowed',
					isDragOver && !disabled && !error && 'border-gold bg-gold/5 scale-[1.01]',
					error && 'border-error/60 bg-error/5',
					!isDragOver && !error && !disabled && 'border-navy-200 hover:border-navy-400 hover:bg-cream/30',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 focus-visible:ring-offset-2',
				)}
			>
				{/* Upload icon */}
				<div className={cn(
					'mb-3 rounded-full p-3',
					isDragOver ? 'bg-gold/10 text-gold' : 'bg-muted text-muted-foreground',
				)}>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
						<polyline points="17 8 12 3 7 8" />
						<path d="M12 3v12" />
					</svg>
				</div>

				<p className="text-sm font-ui font-medium text-foreground mb-1">
					{isDragOver ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
				</p>

				{hint && (
					<p id="dropzone-hint" className="text-xs text-muted-foreground font-ui text-center max-w-xs">
						{hint}
					</p>
				)}

				{maxSize && (
					<p className="text-[11px] text-muted-foreground/60 font-ui mt-1">
						Max file size: {formatFileSize(maxSize)}
					</p>
				)}

				<input
					ref={inputRef}
					type="file"
					accept={accept}
					multiple={multiple}
					onChange={handleChange}
					className="hidden"
					tabIndex={-1}
					aria-hidden="true"
				/>
			</div>

			{error && (
				<p className="text-xs text-error font-ui">{error}</p>
			)}

			{/* File list */}
			{files.length > 0 && (
				<div className="space-y-2">
					{files.map(file => (
						<div
							key={file.id}
							className={cn(
								'flex items-center gap-3 p-3 rounded-lg border',
								file.error ? 'border-error/40 bg-error/5' : 'border-border bg-card',
							)}
						>
							{/* Preview or file icon */}
							{showPreview && file.preview ? (
								<img
									src={file.preview}
									alt={file.file.name}
									className="w-10 h-10 rounded-md object-cover flex-shrink-0"
								/>
							) : (
								<div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0 text-muted-foreground">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
										<polyline points="14 2 14 8 20 8" />
									</svg>
								</div>
							)}

							{/* File info */}
							<div className="flex-1 min-w-0">
								<p className="text-sm font-ui font-medium text-foreground truncate">
									{file.file.name}
								</p>
								<p className="text-xs text-muted-foreground font-ui">
									{formatFileSize(file.file.size)}
									{file.progress != null && ` — ${Math.round(file.progress)}%`}
								</p>
								{file.error && (
									<p className="text-xs text-error font-ui mt-0.5">{file.error}</p>
								)}
								{file.progress != null && file.progress < 100 && (
									<div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
										<div
											className="h-full bg-navy rounded-full transition-all duration-normal"
											style={{ width: `${file.progress}%` }}
										/>
									</div>
								)}
							</div>

							{/* Remove */}
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation()
									removeFile(file.id)
								}}
								className="flex-shrink-0 p-1 rounded-md text-muted-foreground hover:text-error hover:bg-error/10 transition-colors"
								aria-label={`Remove ${file.file.name}`}
							>
								<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M18 6 6 18" />
									<path d="m6 6 12 12" />
								</svg>
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
